import { memo, useRef, useMemo, useCallback, useEffect, useState, ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { Crud, CrudMode, notification, rejectionError, transformCrudFormErrors, SelectsOptions, getSelectsOptions } from '@ohpen/enso-ui';
import type { CrudFormComponent, CrudProps, ValidationError } from '@ohpen/enso-ui';
import {
  ChangeRequestOfIntermediaryDraftDto,
  IntermediaryDraftDto,
  intermediaryGetById,
  intermediaryCreateChangeRequest,
  IntermediaryResponse,
} from '@pmp/api';
import { crudToActionMap, changeRequestNotificationMessage, changeRequestButtonMessage } from '@pmp/utils';
import { useFeatureToggle } from '@pmp/hooks';
import { CrudFormProps } from '@pmp/types';

import { mapIntermediaryToDto } from '..';
import { FormSchema } from '.';

export const BrokersCrudForm = memo(
  ({ mode: modeProp, record, valueLists, refreshTable, onClose }: CrudFormProps<IntermediaryResponse>): ReactElement | null => {
    const ref = useRef<CrudFormComponent>(null);
    const [open, setOpen] = useState(false);
    const [mode, setMode] = useState<CrudMode | null>(modeProp);
    const { t } = useTranslation();
    const enableFourEyes = useFeatureToggle(['enableFourEyes']);

    useEffect(() => {
      if (mode === CrudMode.EDIT && !record?.id) {
        notification().error({ message: t('global.errors.unexpected') });
        onClose();
        return;
      }
      setOpen(true);
    }, [mode, record, t, onClose]);

    const request = useCallback(async ({ id, mode }: { id: string; mode: CrudMode }) => {
      if (mode === CrudMode.CREATE) return {};
      const { data } = await intermediaryGetById({ path: { id } });
      return data ?? {};
    }, []);

    const mapFormValuesToDraft = useCallback(
      (values: IntermediaryResponse): IntermediaryDraftDto => mapIntermediaryToDto(values, record),
      [record],
    );

    const onConfirm: (formData: IntermediaryResponse) => Promise<ValidationError[] | void> = useCallback(
      (values: IntermediaryResponse) => {
        if (!mode || !crudToActionMap[mode]) return Promise.reject(rejectionError('mode is required'));

        const data = mapFormValuesToDraft(values);
        const body: ChangeRequestOfIntermediaryDraftDto = {
          data,
          actionType: crudToActionMap[mode],
        };

        return intermediaryCreateChangeRequest({ body }).then(
          ({ data: responseData }) => {
            if (!responseData.isValid) {
              return transformCrudFormErrors({ ...responseData.errors });
            }
            if (mode === CrudMode.EDIT) refreshTable();
            notification().success({
              message: changeRequestNotificationMessage(t, enableFourEyes),
            });
          },
        );
      },
      [mode, refreshTable, t, enableFourEyes, mapFormValuesToDraft],
    );

    const handleOnClose = useCallback(() => {
      setOpen(false);
      setTimeout(() => {
        onClose();
      }, 300);
    }, [onClose]);

    const title = useMemo(() => {
      if (!mode) return '';
      const titles: { [key: string]: string } = {
        [CrudMode.VIEW]: t('global.labels.viewX', { x: t('lender.brokers.labels.broker') }),
        [CrudMode.CREATE]: t('global.labels.createX', { x: t('lender.brokers.labels.broker') }),
        [CrudMode.EDIT]: t('global.labels.editX', { x: t('lender.brokers.labels.broker') }),
      };
      return titles[mode];
    }, [mode, t]);

    const selectsOptions: SelectsOptions = useMemo(() => (valueLists && getSelectsOptions(valueLists)) || {}, [valueLists]);

    const crudProps: CrudProps<IntermediaryResponse> = useMemo(() => {
      return {
        ref,
        open,
        title,
        labels: {
          confirm: mode === CrudMode.EDIT ? t('global.labels.save') : changeRequestButtonMessage(t, enableFourEyes),
        },
        params: { id: record?.id, mode },
        request,
        mode,
        customForm: true,
        width: 1100,
        wrapperCol: { flex: 'auto' },
        disableDrawerBodyScroll: true,
        onClose: handleOnClose,
        onConfirm,
      };
    }, [open, title, mode, t, record?.id, request, handleOnClose, onConfirm, enableFourEyes]);

    return (
      <Crud {...crudProps}>
        <FormSchema form={ref.current?.form} mode={mode ?? CrudMode.VIEW} selectsOptions={selectsOptions} />
      </Crud>
    );
  },
);
